import { Link } from "react-router-dom";
import { ShieldCheck, Mail, ArrowRight } from "lucide-react";
import WaveBackground from "@/components/site/WaveBackground";
import useSEO from "@/lib/useSEO";

// Update this whenever the policy changes.
const LAST_UPDATED = "20 May 2026";

const sections = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    body: (
      <>
        <p>
          Fenco Digital (&ldquo;<strong>Fenco</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;, &ldquo;<strong>our</strong>&rdquo;) is a Northern Ireland-based business that helps UK companies reduce costs and grow through card payment solutions, point-of-sale (POS) systems, website design and app design.
        </p>
        <p>
          For the purposes of the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, Fenco Digital is the &ldquo;<strong>data controller</strong>&rdquo; of any personal information you provide to us through this website.
        </p>
        <p>
          If you have any questions about this policy or how we handle your data, contact us at{" "}
          <a href="mailto:info@fencodigital.com" className="text-[#FF5A1F] font-semibold hover:underline">info@fencodigital.com</a>{" "}
          or on <a href="tel:07426821449" className="text-[#FF5A1F] font-semibold hover:underline">07426 821449</a>.
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: "2. What information we collect",
    body: (
      <>
        <p>
          We only collect what we need to respond to your enquiry and, where relevant, match you with the right payment or technology partner. That includes:
        </p>
        <ul>
          <li><strong>Contact details</strong> &mdash; your name, email address, phone number and (optionally) your business name and postcode.</li>
          <li><strong>Business details</strong> &mdash; annual card turnover band, whether you already have a card machine and which service you&rsquo;re interested in (Card Payments, POS Systems, Website Design, App Design or a bundle).</li>
          <li><strong>Anything else you tell us</strong> &mdash; the free-text message field on our contact form.</li>
          <li><strong>Technical data</strong> &mdash; basic information collected automatically by our hosting and analytics tools, such as IP address, device and browser, pages viewed and timestamps.</li>
        </ul>
        <p>We do not knowingly collect data from anyone under the age of 18.</p>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    title: "3. How we use your information",
    body: (
      <>
        <p>We use your information to:</p>
        <ul>
          <li>Reply to your enquiry and send you the quote or comparison you asked for.</li>
          <li>Match you with the payment, POS or design partner that best suits your business.</li>
          <li>Keep a record of our conversations so we can support you on go-live day and afterwards.</li>
          <li>Improve our website and marketing (aggregated, non-identifying analytics).</li>
          <li>Meet our legal, accounting and regulatory obligations.</li>
        </ul>
      </>
    ),
  },
  {
    id: "lawful-basis",
    title: "4. Our lawful basis for processing",
    body: (
      <>
        <p>Under UK GDPR we rely on the following lawful bases:</p>
        <ul>
          <li><strong>Consent</strong> &mdash; when you fill in our contact or quote form, you&rsquo;re asking us to get in touch. You can withdraw that consent at any time (see &ldquo;Your rights&rdquo; below).</li>
          <li><strong>Legitimate interests</strong> &mdash; running our business, following up on active enquiries, and improving our services.</li>
          <li><strong>Legal obligation</strong> &mdash; where we&rsquo;re required by law (e.g. tax, accounting, fraud prevention).</li>
        </ul>
      </>
    ),
  },
  {
    id: "sharing",
    title: "5. Who we share your information with",
    body: (
      <>
        <p>
          We <strong>never sell</strong> your data. We only share it with the following, on a strictly need-to-know basis:
        </p>
        <ul>
          <li>
            <strong>Service providers</strong> we use to run the business &mdash; our website host, email provider, cloud database, and any analytics tools. These providers are contractually required to protect your data and only process it on our instructions.
          </li>
          <li>
            <strong>Payment and technology partners</strong> &mdash; with your permission, we&rsquo;ll introduce you to the payment or POS partner that best fits your business. We&rsquo;ll always tell you which partner before we share anything.
          </li>
          <li>
            <strong>Authorities and regulators</strong> &mdash; where the law requires us to (for example, to respond to a lawful request from HMRC, a court or the Information Commissioner&rsquo;s Office).
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    title: "6. Cookies & analytics",
    body: (
      <>
        <p>
          This website uses a small number of cookies and similar technologies to help us understand how visitors use the site so we can improve it. This is limited to standard analytics such as page views, referral source and general location (country/city, not precise location).
        </p>
        <p>
          You can control cookies through your browser settings. Refusing analytics cookies will not affect your ability to use the site or submit an enquiry.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "7. How long we keep your data",
    body: (
      <>
        <p>We keep your personal data only for as long as we need it:</p>
        <ul>
          <li><strong>Enquiries that don&rsquo;t lead to a customer relationship</strong> &mdash; up to 24 months from your last contact with us, then deleted.</li>
          <li><strong>Active customers</strong> &mdash; for the duration of our working relationship, plus a further 7 years to meet our legal, tax and accounting obligations.</li>
          <li><strong>Website analytics</strong> &mdash; typically 12&ndash;26 months in aggregated form.</li>
        </ul>
      </>
    ),
  },
  {
    id: "security",
    title: "8. How we protect your data",
    body: (
      <>
        <p>
          Your data is stored in a secured cloud database with encryption in transit (HTTPS/TLS). Access is restricted to authorised personnel on a need-to-know basis, and we use industry-standard practices to keep it safe.
        </p>
        <p>
          If a data breach occurs that&rsquo;s likely to affect your rights and freedoms, we will notify you and the Information Commissioner&rsquo;s Office (ICO) within 72 hours, as required by UK GDPR.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "9. Your rights",
    body: (
      <>
        <p>You have the following rights under UK GDPR. All are free of charge and we&rsquo;ll respond within one month:</p>
        <ul>
          <li><strong>Right of access</strong> &mdash; a copy of the personal data we hold on you.</li>
          <li><strong>Right to rectification</strong> &mdash; correcting inaccurate or incomplete data.</li>
          <li><strong>Right to erasure</strong> (&ldquo;right to be forgotten&rdquo;) &mdash; asking us to delete your data.</li>
          <li><strong>Right to restrict processing</strong> &mdash; limiting how we use your data.</li>
          <li><strong>Right to object</strong> &mdash; to processing based on legitimate interests, and to direct marketing at any time.</li>
          <li><strong>Right to data portability</strong> &mdash; receiving your data in a portable, machine-readable format.</li>
          <li><strong>Right to withdraw consent</strong> &mdash; at any time, where we&rsquo;re relying on consent.</li>
        </ul>
        <p>
          To exercise any of these rights, email us at{" "}
          <a href="mailto:info@fencodigital.com" className="text-[#FF5A1F] font-semibold hover:underline">info@fencodigital.com</a>.
        </p>
        <p>
          You also have the right to complain to the Information Commissioner&rsquo;s Office (ICO), the UK&rsquo;s data-protection regulator, at{" "}
          <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#FF5A1F] font-semibold hover:underline">ico.org.uk</a> or on <span className="whitespace-nowrap">0303 123 1113</span>. We&rsquo;d appreciate the chance to sort things out with you first, though.
        </p>
      </>
    ),
  },
  {
    id: "international",
    title: "10. International transfers",
    body: (
      <>
        <p>
          Where any of our service providers process data outside the UK, we ensure appropriate safeguards are in place &mdash; typically the UK International Data Transfer Agreement or equivalent standard contractual clauses recognised by the ICO.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "11. Changes to this policy",
    body: (
      <>
        <p>
          We may update this policy from time to time to reflect changes in the law, our services, or the tools we use. The &ldquo;last updated&rdquo; date at the top of this page will always show when it was last changed.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "12. Get in touch",
    body: (
      <>
        <p>Questions, requests or complaints about this policy? We&rsquo;d love to help.</p>
        <ul>
          <li><strong>Email:</strong> <a href="mailto:info@fencodigital.com" className="text-[#FF5A1F] font-semibold hover:underline">info@fencodigital.com</a></li>
          <li><strong>Phone:</strong> <a href="tel:07426821449" className="text-[#FF5A1F] font-semibold hover:underline">07426 821449</a></li>
        </ul>
      </>
    ),
  },
];

export default function Privacy() {
  useSEO({
    title: "Privacy Policy | Fenco Digital",
    description: "How Fenco Digital collects, uses and protects your personal data under the UK GDPR. Your rights, our lawful basis, retention periods and how to get in touch.",
    path: "/privacy",
  });

  return (
    <div data-testid="privacy-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <WaveBackground variant="accent" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-6">
            <ShieldCheck size={14} className="text-[#FF5A1F]" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF5A1F]">Privacy · UK GDPR</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-gray-900">
            Privacy policy<span className="text-[#FF5A1F]">.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed">
            Short version: we only collect what we need to help you, we never sell your data, and you can ask us to delete it at any time.
          </p>
          <p className="mt-4 text-sm text-gray-500" data-testid="privacy-last-updated">
            Last updated: <strong className="text-gray-700">{LAST_UPDATED}</strong>
          </p>
        </div>
      </section>

      {/* CONTENTS + BODY */}
      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          {/* Table of contents */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start" data-testid="privacy-toc">
            <div className="bg-[#F8F9FA] rounded-2xl border border-gray-100 p-6">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF5A1F]">Contents</p>
              <nav className="mt-4 space-y-2">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    data-testid={`privacy-toc-${s.id}`}
                    className="block text-sm text-gray-700 hover:text-[#FF5A1F] font-medium transition"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-8 prose-fenco" data-testid="privacy-body">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-28 mb-12" data-testid={`privacy-section-${s.id}`}>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">{s.title}</h2>
                <div className="mt-5 text-gray-700 leading-relaxed space-y-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul_li]:leading-relaxed [&_p]:leading-relaxed">
                  {s.body}
                </div>
              </section>
            ))}
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0a0a0a] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Mail className="mx-auto text-[#FF5A1F] mb-6" size={32} />
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Questions about your data<span className="text-[#FF5A1F]">?</span>
          </h2>
          <p className="mt-5 text-lg text-gray-300 max-w-xl mx-auto">
            Drop us a line at info@fencodigital.com and we&rsquo;ll come back to you within one working day.
          </p>
          <Link
            to="/contact"
            data-testid="privacy-final-cta"
            className="mt-9 inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/30"
          >
            Contact us <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
